import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { ApiError } from '@/shared/api/api-error';
import { formatDateTime } from '@/shared/lib/format';

import { useSaveLoyaltyRules } from '../api/loyalty.queries';
import { loyaltyRulesSchema, type LoyaltyRulesFormValues } from '../schemas/loyalty.schema';
import type { LoyaltyRule, LoyaltyRules } from '../types/loyalty.types';

const FIELDS = [
  'registrationPoints',
  'registrationEnabled',
  'defaultPurchasePoints',
  'defaultPurchaseEnabled',
] as const;

function toValues(rules: LoyaltyRules): LoyaltyRulesFormValues {
  return {
    registrationPoints: rules.registration.points,
    registrationEnabled: rules.registration.isActive,
    defaultPurchasePoints: rules.defaultPurchase.points,
    defaultPurchaseEnabled: rules.defaultPurchase.isActive,
  };
}

function lastChange(rule: LoyaltyRule) {
  if (!rule.updatedAt) return null;
  return `Last changed ${formatDateTime(rule.updatedAt)}${rule.updatedByName ? ` by ${rule.updatedByName}` : ''}`;
}

/**
 * The country's registration and default purchase rules, saved together. A disabled rule awards
 * nothing; a disabled default purchase rule leaves only the brand rules.
 */
export function BaseRulesCard({
  countryName,
  rules,
  canManage,
}: {
  countryName: string;
  rules: LoyaltyRules;
  canManage: boolean;
}) {
  const save = useSaveLoyaltyRules(rules.countryId);
  const form = useForm<LoyaltyRulesFormValues>({
    resolver: zodResolver(loyaltyRulesSchema),
    defaultValues: toValues(rules),
  });
  const { reset } = form;
  useEffect(() => reset(toValues(rules)), [rules, reset]);

  const { error } = save;
  useEffect(() => {
    if (!(error instanceof ApiError) || !error.fieldErrors) return;
    for (const [field, message] of Object.entries(error.fieldErrors)) {
      const key = FIELDS.find((name) => name.toLowerCase() === field.toLowerCase());
      if (key) form.setError(key, { type: 'server', message });
    }
  }, [error, form]);
  const formError = error instanceof ApiError && !error.fieldErrors ? error.message : null;

  const pointsField = (
    name: 'registrationPoints' | 'defaultPurchasePoints',
    label: string,
    description: string,
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              disabled={!canManage}
              value={Number.isFinite(field.value) ? field.value : ''}
              onChange={(event) =>
                field.onChange(event.target.value === '' ? Number.NaN : Number(event.target.value))
              }
            />
          </FormControl>
          <FormDescription>{description}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const enabledField = (name: 'registrationEnabled' | 'defaultPurchaseEnabled', label: string) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex items-center gap-2 space-y-0">
          <FormControl>
            <Switch checked={field.value} onCheckedChange={field.onChange} disabled={!canManage} />
          </FormControl>
          <FormLabel className="!mt-0">{label}</FormLabel>
        </FormItem>
      )}
    />
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Earning rules</CardTitle>
        <CardDescription className="text-xs">
          Points customers in {countryName} earn. Changes apply to the next registration or
          purchase.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            noValidate
            className="grid gap-6 text-xs md:grid-cols-2"
            onSubmit={form.handleSubmit((values) => save.mutate(values))}
          >
            <fieldset className="space-y-3">
              <legend className="mb-2 text-sm font-medium">Registration</legend>
              {pointsField(
                'registrationPoints',
                'Points for a new account',
                'Awarded once per customer, after registration succeeds.',
              )}
              {enabledField('registrationEnabled', 'Award registration points')}
              {lastChange(rules.registration) && (
                <p className="text-muted-foreground">{lastChange(rules.registration)}</p>
              )}
            </fieldset>
            <fieldset className="space-y-3">
              <legend className="mb-2 text-sm font-medium">Default purchase</legend>
              {pointsField(
                'defaultPurchasePoints',
                'Points per purchased item',
                'Used for every brand without its own rule below.',
              )}
              {enabledField('defaultPurchaseEnabled', 'Award default purchase points')}
              {lastChange(rules.defaultPurchase) && (
                <p className="text-muted-foreground">{lastChange(rules.defaultPurchase)}</p>
              )}
            </fieldset>
            {formError && (
              <p role="alert" className="text-destructive md:col-span-2">
                {formError}
              </p>
            )}
            {canManage && (
              <div className="flex justify-end md:col-span-2">
                <Button
                  type="submit"
                  size="sm"
                  disabled={save.isPending || !form.formState.isDirty}
                >
                  {save.isPending && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
                  Save earning rules
                </Button>
              </div>
            )}
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
